const SWIPER_MODULE_URL = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.mjs';
const SWIPER_STYLES_URL = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css';
let swiperStylesPromise;

function loadSwiperStyles() {
    if (swiperStylesPromise) return swiperStylesPromise;
    if (document.querySelector(`link[href="${SWIPER_STYLES_URL}"]`)) return Promise.resolve();

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = SWIPER_STYLES_URL;
    swiperStylesPromise = new Promise((resolve, reject) => {
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', () => {
            link.remove();
            swiperStylesPromise = null;
            reject(new Error('Unable to load Swiper styles'));
        }, { once: true });
        document.head.append(link);
    });
    return swiperStylesPromise;
}

class CollageSlider extends HTMLElement {
    connectedCallback() {
        this.connectionVersion = (this.connectionVersion || 0) + 1;
        this.events = new AbortController();
        this.userPaused = false;
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const options = { signal: this.events.signal };

        this.addEventListener('shopify:block:select', (event) => this.selectBlock(event), options);
        this.addEventListener('shopify:block:deselect', () => {
            this.editorSelected = false;
            this.updateAutoplay();
        }, options);
        this.addEventListener('focusin', () => this.updateAutoplay(), options);
        this.addEventListener('focusout', () => {
            queueMicrotask(() => this.updateAutoplay());
        }, options);
        this.addEventListener('mouseenter', () => this.updateAutoplay(), options);
        this.addEventListener('mouseleave', () => this.updateAutoplay(), options);
        this.addEventListener('click', (event) => {
            if (!event.target.closest('[data-collage-autoplay]')) return;
            this.userPaused = !this.userPaused;
            this.updateAutoplay();
        }, options);
        this.motionPreference.addEventListener('change', () => this.updateAutoplay(), options);

        if ('IntersectionObserver' in window) {
            this.intersectionObserver = new IntersectionObserver((entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                this.intersectionObserver.disconnect();
                this.initSwiper();
            }, { rootMargin: '600px 0px' });
            this.intersectionObserver.observe(this);
        } else {
            this.initFrame = requestAnimationFrame(() => this.initSwiper());
        }
    }

    disconnectedCallback() {
        this.connectionVersion += 1;
        this.events?.abort();
        this.intersectionObserver?.disconnect();
        cancelAnimationFrame(this.initFrame);
        this.swiper?.destroy(true, true);
        this.swiper = null;
        this.initPromise = null;
        this.editorSelected = false;
    }

    initSwiper() {
        if (this.swiper) return Promise.resolve(this.swiper);
        if (this.initPromise) return this.initPromise;
        const version = this.connectionVersion;
        this.initPromise = this.createSwiper(version).finally(() => {
            if (version === this.connectionVersion) this.initPromise = null;
        });
        return this.initPromise;
    }

    async createSwiper(version) {
        try {
            const config = this.querySelector('[data-collage-settings]');
            if (!config) return;
            const settings = JSON.parse(config.textContent);
            const [{ default: Swiper }] = await Promise.all([
                import(SWIPER_MODULE_URL),
                loadSwiperStyles(),
            ]);
            if (!this.isConnected || version !== this.connectionVersion) return;

            this.scrollLeft = 0;
            this.swiper = new Swiper(this, {
                slidesPerView: 1,
                spaceBetween: settings.spaceBetweenMobile,
                loop: false,
                watchOverflow: true,
                grabCursor: true,
                a11y: {
                    slideLabelMessage: this.dataset.slideLabel.replace('[index]', '{{index}}').replace('[length]', '{{slidesLength}}'),
                    paginationBulletMessage: this.dataset.pageLabel.replace('[page]', '{{index}}'),
                },
                pagination: {
                    el: this.querySelector('[data-collage-pagination]'),
                    clickable: true,
                    renderBullet: (index, className) => `<button type="button" class="${className}"></button>`,
                },
                autoplay: {
                    enabled: settings.autoplay && !window.Shopify?.designMode,
                    delay: settings.autoplayDelay * 1000,
                    disableOnInteraction: false,
                },
                breakpoints: {
                    768: {
                        slidesPerView: settings.slidesPerViewTablet,
                        spaceBetween: settings.spaceBetween,
                    },
                    1200: {
                        slidesPerView: settings.slidesPerView,
                        spaceBetween: settings.spaceBetween,
                    },
                },
                on: {
                    lock: () => this.updateAutoplay(),
                    unlock: () => this.updateAutoplay(),
                },
            });
            this.autoplayEnabled = settings.autoplay && !window.Shopify?.designMode;
            this.updateAutoplay();
            return this.swiper;
        } catch (error) {
            console.error('Unable to initialize collage slider', error);
        }
    }

    updateAutoplay() {
        if (!this.swiper || !this.isConnected) return;
        const button = this.querySelector('[data-collage-autoplay]');
        const enabled = this.autoplayEnabled && !this.motionPreference.matches && !this.swiper.isLocked;
        if (button) {
            button.hidden = !enabled;
            button.textContent = this.userPaused ? button.dataset.play : button.dataset.pause;
        }

        const paused = !enabled || this.userPaused || this.editorSelected
            || this.matches(':focus-within') || this.matches(':hover');
        if (paused) {
            this.swiper.autoplay.stop();
        } else if (!this.swiper.autoplay.running) {
            this.swiper.autoplay.start();
        }
    }

    async selectBlock(event) {
        const slide = Array.from(this.querySelectorAll('[data-collage-block]'))
            .find((item) => item.dataset.collageBlock === event.detail?.blockId);
        if (!slide) return;
        this.editorSelected = true;
        const swiper = await this.initSwiper();
        if (!swiper || swiper.destroyed || !this.isConnected) return;
        swiper.slideTo(Array.from(swiper.slides).indexOf(slide), 0);
        this.updateAutoplay();
    }
}

if (!customElements.get('collage-slider')) {
    customElements.define('collage-slider', CollageSlider);
}
